import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';


const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by creation date (newest first)
      usersData.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      setUsers(usersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesFilter = filter === 'all' || user.role === filter;
    const matchesSearch = !searchTerm ||
      user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);

    return matchesFilter && matchesSearch;
  });

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      customer: 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  // Fetch user's orders
  const fetchUserOrders = async (userId, userEmail) => {
    try {
      setLoadingOrders(true);
      // Query orders by userId or customerEmail
      const ordersRef = collection(db, 'orders');
      const snapshot = await getDocs(ordersRef);

      const orders = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(order => order.userId === userId || order.customerEmail === userEmail)
        .sort((a, b) => {
          const dateA = a.createdAt?.seconds || 0;
          const dateB = b.createdAt?.seconds || 0;
          return dateB - dateA;
        });

      setUserOrders(orders);
    } catch (error) {
      console.error('Error fetching user orders:', error);
      toast.error('Failed to fetch user orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  // Handle view user - fetch orders
  const handleViewUser = async (user) => {
    setSelectedUser(user);
    await fetchUserOrders(user.id, user.email);
  };

  // Helper function to format date from ISO string or Firestore timestamp
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';

    try {
      let date;

      // Check if it's a Firestore Timestamp object (has .seconds property)
      if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        date = new Date(dateValue.seconds * 1000);
      }
      // Check if it's an ISO string
      else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      }
      // Check if it's already a Date object
      else if (dateValue instanceof Date) {
        date = dateValue;
      }
      else {
        return 'N/A';
      }

      // Verify it's a valid date
      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'N/A';
    }
  };

  // Get order status color
  const getOrderStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8 md:pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif text-deep-brown">Users</h1>
          <p className="text-sm md:text-base text-warm-gray font-medium">Manage user accounts and roles</p>
        </div>



        {/* Search and Filters */}
        <div className="mb-4 md:mb-6 flex flex-col gap-3 md:gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 text-sm md:text-base"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 md:px-4 py-2 rounded-md text-xs md:text-sm ${filter === 'all'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              All Users ({users.length})
            </button>
            <button
              onClick={() => setFilter('admin')}
              className={`px-3 md:px-4 py-2 rounded-md text-xs md:text-sm ${filter === 'admin'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              Admins ({users.filter(u => u.role === 'admin').length})
            </button>
            <button
              onClick={() => setFilter('customer')}
              className={`px-3 md:px-4 py-2 rounded-md text-xs md:text-sm ${filter === 'customer'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              Customers ({users.filter(u => u.role === 'customer').length})
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Contact
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Joined
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 md:h-10 md:w-10 flex-shrink-0">
                          <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <span className="text-xs md:text-sm font-medium text-amber-800">
                              {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3 md:ml-4">
                          <div className="text-xs md:text-sm font-medium text-gray-900">
                            {user.fullName || 'No Name'}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500 truncate max-w-[150px] md:max-w-none">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="text-xs md:text-sm text-gray-900">{user.phone || 'N/A'}</div>
                      <div className="text-xs md:text-sm text-gray-500">{user.address || 'No address'}</div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                        {user.role || 'customer'}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden sm:table-cell">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm font-medium">
                      <button
                        onClick={() => handleViewUser(user)}
                        className="text-amber-600 hover:text-amber-700 font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchTerm ? 'No users found matching your search.' : 'No users found for the selected filter.'}
            </p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Total Users</h3>
            <p className="text-2xl md:text-3xl font-bold text-amber-600">{users.length}</p>
            <p className="text-xs md:text-sm text-gray-600 mt-1">
              Registered users on the platform
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Admin Users</h3>
            <p className="text-2xl md:text-3xl font-bold text-red-600">
              {users.filter(u => u.role === 'admin').length}
            </p>
            <p className="text-xs md:text-sm text-gray-600 mt-1">
              Users with admin privileges
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Recent Signups</h3>
            <p className="text-2xl md:text-3xl font-bold text-green-600">
              {users.filter(u => {
                if (!u.createdAt) return false;
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);

                let createdDate;
                if (u.createdAt && typeof u.createdAt === 'object' && 'seconds' in u.createdAt) {
                  createdDate = new Date(u.createdAt.seconds * 1000);
                } else if (typeof u.createdAt === 'string') {
                  createdDate = new Date(u.createdAt);
                }

                return createdDate && createdDate > weekAgo;
              }).length}
            </p>
            <p className="text-xs md:text-sm text-gray-600 mt-1">
              New users in the last 7 days
            </p>
          </div>
        </div>

        {/* User Detail Modal */}
        <AnimatePresence>
          {selectedUser && (
            <motion.div
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedUser(null)}
            >
              <motion.div
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-200 p-6 flex justify-between items-center">
                  <h2 className="text-2xl font-serif font-bold text-deep-brown">User Details</h2>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 space-y-6">
                  {/* Profile Section */}
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="h-16 w-16 rounded-full bg-amber-100 flex-shrink-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-amber-800">
                        {selectedUser.fullName ? selectedUser.fullName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-deep-brown">{selectedUser.fullName || 'No Name'}</h3>
                      <p className="text-warm-gray">{selectedUser.email}</p>
                      <div className="mt-2">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getRoleColor(selectedUser.role)}`}>
                          {selectedUser.role || 'customer'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="font-bold text-deep-brown mb-3">Contact Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-warm-gray font-medium">Email</p>
                        <p className="text-gray-900">{selectedUser.email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-warm-gray font-medium">Phone</p>
                        <p className="text-gray-900">{selectedUser.phone || 'N/A'}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-sm text-warm-gray font-medium">Address</p>
                        <p className="text-gray-900">{selectedUser.address || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Account Information */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="font-bold text-deep-brown mb-3">Account Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-warm-gray font-medium">Account ID</p>
                        <p className="text-gray-900 text-sm font-mono">{selectedUser.id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-warm-gray font-medium">Joined Date</p>
                        <p className="text-gray-900">{formatDate(selectedUser.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-warm-gray font-medium">Account Status</p>
                        <p className="text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Orders Section */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="font-bold text-deep-brown mb-3">Orders ({userOrders.length})</h4>
                    {loadingOrders ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
                      </div>
                    ) : userOrders.length === 0 ? (
                      <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-600 text-sm">
                        <p>No orders found for this user</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {userOrders.map((order) => (
                          <div key={order.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-gray-900">Order #{order.id.substring(0, 8)}</p>
                                <p className="text-xs text-gray-600">{formatDate(order.createdAt)}</p>
                              </div>
                              <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getOrderStatusColor(order.status)}`}>
                                {order.status || 'pending'}
                              </span>
                            </div>

                            {/* Order Items */}
                            {order.items && order.items.length > 0 && (
                              <div className="mb-3 text-sm">
                                <p className="text-gray-700 font-medium mb-1">Items: {order.items.length}</p>
                                <div className="space-y-1 text-xs text-gray-600">
                                  {order.items.slice(0, 2).map((item, idx) => (
                                    <p key={idx}>
                                      {item.title || 'Product'} × {item.quantity || 1} - ₹{((item.price || 0) * (item.quantity || 1)).toLocaleString()}
                                    </p>
                                  ))}
                                  {order.items.length > 2 && (
                                    <p className="text-gray-500">+{order.items.length - 2} more items</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Order Total */}
                            <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                              <span className="font-semibold text-gray-900">Total:</span>
                              <span className="font-bold text-lg text-amber-600">₹{(order.total || 0).toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Additional Info */}
                  <div className="border-t border-gray-200 pt-4 bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600">
                      <strong>Last Updated:</strong> {formatDate(selectedUser.updatedAt) || formatDate(selectedUser.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-3">
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="px-4 py-2 rounded-md bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 font-medium"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminUsers;